import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Chip,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close,
  TableChart,
  CheckCircle,
  Functions,
  VerifiedUser,
  ArrowForward,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
}

const ExportModal: React.FC<ExportModalProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [company, setCompany] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const device = typeof window !== 'undefined' && window.innerWidth <= 768 ? 'mobile' : 'desktop';

    if (!email || !email.includes('@')) {
      if (typeof window !== 'undefined' && (window as any).umami) {
        (window as any).umami.track('SignupEmptyAttempt', { source: 'export_modal', device });
      }
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const companyDisplay = company ? `${company} (Excel Export Early Access)` : 'Individual (Excel Export Early Access)';
      await apiService.createBetaSignup({
        email,
        first_name: firstName,
        company: companyDisplay,
        signup_source: 'beta-section',
      });

      if (typeof window !== 'undefined' && (window as any).umami) {
        (window as any).umami.track('SignupSubmitted', {
          source: 'export_modal',
          device,
          has_first_name: Boolean(firstName),
          has_company: Boolean(company),
          status: 'success',
        });
      }

      setIsSuccess(true);
      setEmail('');
      setFirstName('');
      setCompany('');
    } catch (err) {
      if (typeof window !== 'undefined' && (window as any).umami) {
        (window as any).umami.track('SignupFailed', { source: 'export_modal', device, error: 'api_error' });
      }
      setErrorMessage('Failed to register. Please try again.');
      console.error('Export modal signup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsSuccess(false);
    setErrorMessage('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: { xs: 1, sm: 2 },
          bgcolor: '#ffffff',
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: 'rgba(4, 120, 87, 0.08)',
              border: '1px solid rgba(4, 120, 87, 0.2)',
              display: 'flex',
            }}
          >
            <TableChart sx={{ color: '#047857', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 800, lineHeight: 1.2 }}>
              Audit-Ready Excel Exports
            </Typography>
            <Typography variant="caption" sx={{ color: '#047857', fontWeight: 700, display: 'block' }}>
              LIVE POPULATED FORMULAS
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleModalClose} sx={{ color: '#64748b' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* Core Value Proposition Box */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            bgcolor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 3,
          }}
        >
          <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, mb: 1 }}>
            FiMod handles Excel exports with fully populated live formulas for seamless auditing.
          </Typography>
          <Typography variant="body2" sx={{ color: '#475569', mb: 2, lineHeight: 1.6 }}>
            Unlike legacy software that exports hardcoded numbers, FiMod generates native Microsoft Excel (<code style={{ color: '#047857' }}>.xlsx</code>) workbooks with dynamic cell formulas so your Investment Committee, Lenders, and Valuation Auditors can inspect every calculation line.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Functions sx={{ color: '#047857', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600 }}>
                Populated Native Formulas (<code style={{ color: '#1e40af' }}>=XIRR()</code>, <code style={{ color: '#1e40af' }}>=PMT()</code>, <code style={{ color: '#1e40af' }}>=INDEX/MATCH</code>)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircle sx={{ color: '#047857', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600 }}>
                Complete Cell Auditability & Transparency (Zero Hardcoded Output)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <VerifiedUser sx={{ color: '#047857', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600 }}>
                UK Investment Committee & Lender Compliant Formatting
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Beta Signup Form inside Modal */}
        {!isSuccess ? (
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 700, mb: 0.5 }}>
              Request Early Access & Sample Excel Export Workbook
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
              Be the first to test FiMod's dynamic Excel export engine.
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="First Name"
                    variant="outlined"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    type="email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Company Name (Optional)"
                    variant="outlined"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                    endIcon={!isLoading && <ArrowForward />}
                    sx={{ py: 1.2 }}
                  >
                    {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Get Sample Excel Export & Beta Access'}
                  </Button>
                </Grid>
              </Grid>
            </form>

            {errorMessage && (
              <Typography variant="caption" sx={{ mt: 1.5, display: 'block', color: 'error.main' }}>
                {errorMessage}
              </Typography>
            )}
          </Box>
        ) : (
          <Alert severity="success" sx={{ bgcolor: '#ecfdf5', color: '#047857', fontWeight: 700, borderRadius: 2 }}>
            Thank you! Your request for early Excel export access has been submitted. We will send a sample workbook to your email.
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
